import {
  CLOSED_NOTEHEAD_PATH,
  OPEN_NOTEHEAD_PATH,
  WHOLE_NOTEHEAD_PATH
} from "./noteheadPaths";

import {
  SHARP_PATH,
  FLAT_PATH,
  DOUBLE_SHARP_PATH,
  DOUBLE_FLAT_PATH,
  NATURAL_PATH
} from "./accidentalPaths";

import {
  QUARTER_REST_PATH,
  HALF_REST_PATH,
  WHOLE_REST_PATH,
  EIGHTH_REST_PATH,
  SIXTEENTH_REST_PATH,
  THIRTY_SECOND_REST_PATH
} from "./restPaths";

import {
  EIGHTH_FLAG_PATH,
  SIXTEENTH_FLAG_PATH,
  THIRTY_SECOND_FLAG_PATH
} from "./flagPaths";

const NOTE_SCALE = 0.07;
const NOTEHEAD_SCALE = 0.035;
const HALFNOTE_SCALE = 0.033;
const REST_SCALE = 0.035;

const FLAG_CONFIG = {
  eighth_note: {
    path: EIGHTH_FLAG_PATH,
    up:    "translate(11.9, -35.5) scale(0.03, 0.03)",
    down:  "translate(1, 35.5) scale(0.03, -0.03)"
  },
  sixteenth_note: {
    path: SIXTEENTH_FLAG_PATH,
    up:    "translate(11.9, -35.5) scale(0.03, -0.03)",
    down:  "translate(1, 35.5) scale(0.03, 0.03)"
  },
  thirty_second_note: {
    path: THIRTY_SECOND_FLAG_PATH,
    up:    "translate(11.9, -35.5) scale(0.03, 0.03)",
    down:  "translate(1, 35.5) scale(0.03, -0.03)"
  }
};

export function Glyph({ symbol, fill = "#000", stemUp, accidental, accidentalOnly = false, flagOnly = false, suppressStemAndFlag = false, suppressFlag = false, accidentalOffsetX }) {
  if (typeof symbol !== "string") return null;

  const RHYTHM_PATTERNS = {
    dotted_eighth_three_sixteenths: ["8.", "16", "16", "16"],
    "eighth_four": ["8", "8", "8", "8"],
    "1e1e1e": ["8", "8", "8"],
    "2s1e1e": ["16", "16", "8", "8"],
    "1e2s1e": ["8", "16", "16", "8"],
    "1e1e2s": ["8", "8", "16", "16"],
    "2s2s1e": ["16", "16", "16", "16", "8"],
    "2s1e2s": ["16", "16", "8", "16", "16"],
    "1e2s2s": ["8", "16", "16", "16", "16"],
    "2s2s2s": ["16", "16", "16", "16", "16", "16"]
  };

  if (RHYTHM_PATTERNS[symbol]) {
    return (
      <BeamedRhythmGlyph
        fill={fill}
        stemUp={stemUp ?? true}
        pattern={RHYTHM_PATTERNS[symbol]}
      />
    );
  }

  if (symbol === "eighth_pair") {
    return <EighthPairGlyph fill={fill} stemUp={stemUp ?? true} />;
  }

  if (symbol === "sixteenth_sixteenth_eighth") {
    return <BeamedRhythmGlyph
      fill={fill}
      stemUp={stemUp ?? true}
      pattern={["16", "16", "8"]}
    />;
  }

  if (symbol === "eighth_sixteenth_sixteenth") {
    return <BeamedRhythmGlyph
      fill={fill}
      stemUp={stemUp ?? true}
      pattern={["8", "16", "16"]}
    />;
  }

  if (symbol === "sixteenth_four") {
    return <BeamedRhythmGlyph
      fill={fill}
      stemUp={stemUp ?? true}
      pattern={["16", "16", "16", "16"]}
    />;
  }

  if (symbol.endsWith("_note")) {
    return <NoteGlyph type={symbol} fill={fill} stemUp={stemUp} accidental={accidental} accidentalOnly={accidentalOnly} flagOnly={flagOnly} suppressStemAndFlag={suppressStemAndFlag} suppressFlag={suppressFlag} accidentalOffsetX={accidentalOffsetX}/>;
  }

  if (symbol.endsWith("_rest")) {
    return <RestGlyph type={symbol} fill={fill} />;
  }

  return null;
}

function BeamedRhythmGlyph({ fill, stemUp = true, pattern = [] }) {
  const STEM_HEIGHT = 34;
  const STEM_WIDTH = 1;
  const HEAD_SPACING = 16;
  const BEAM_THICKNESS = 4;

  const stemX = stemUp ? 10.7 : -0.1;
  const stemY = stemUp ? -35.5 : 1.5;
  const beamY1 = stemUp ? -35.5 : 35.5;
  const beamY2 = stemUp ? -29.5 : 29.5;

  // Leave room for an augmentation dot before the next notehead.
  const noteX = i => i * HEAD_SPACING + pattern.slice(0, i).filter(dur => dur.endsWith(".")).length * 8;
  const lastX = noteX(pattern.length - 1);
  const beamX = stemX;
  const beamWidth = lastX;

  function hasSecondBeam(i) {
    return pattern[i] === "16";
  }

  return (
    <g>
      {pattern.map((dur, i) => {
        const x = noteX(i);

        return (
          <g key={i} transform={`translate(${x}, 0)`}>
            <path
              d={CLOSED_NOTEHEAD_PATH}
              fill={fill}
              transform={`scale(${NOTEHEAD_SCALE}, -${NOTEHEAD_SCALE})`}
            />

            {dur.endsWith(".") && <circle cx={17} cy={-4} r={1.8} fill={fill} />}

            <rect
              x={stemX}
              y={stemY}
              width={STEM_WIDTH}
              height={STEM_HEIGHT}
              fill={fill}
            />
          </g>
        );
      })}

      {/* primary beam */}
      <rect
        x={beamX}
        y={beamY1}
        width={beamWidth}
        height={BEAM_THICKNESS}
        fill={fill}
      />

      {/* secondary 16th beams */}
      {pattern.map((dur, i) => {
        if (!hasSecondBeam(i)) return null;

        const prevIs16 = pattern[i - 1] === "16";
        const nextIs16 = pattern[i + 1] === "16";

        let x1 = noteX(i) + stemX;
        let width = 10;

        if (prevIs16) {
          x1 = noteX(i - 1) + stemX;
          width = noteX(i) - noteX(i - 1);
        } else if (nextIs16) {
          width = noteX(i + 1) - noteX(i);
        }

        return (
          <rect
            key={`beam2-${i}`}
            x={x1}
            y={beamY2}
            width={width}
            height={BEAM_THICKNESS}
            fill={fill}
          />
        );
      })}
    </g>
  );
}

function EighthPairGlyph({ fill, stemUp = true }) {
  const NOTEHEAD_SCALE = 0.035;
  const STEM_HEIGHT = 34;
  const STEM_WIDTH = 1;

  const HEAD_SPACING = 20; // distance between the two noteheads
  const BEAM_THICKNESS = 4;
  const BEAM_LENGTH = HEAD_SPACING;

  const stemX = stemUp ? 10.7 : -0.1;
  const stemY = stemUp ? -35.5 : 1.5;
  const beamY = stemUp ? -35.5 : 35.5;

  return (
    <g>
      {/* FIRST NOTEHEAD */}
      <path
        d={CLOSED_NOTEHEAD_PATH}
        fill={fill}
        transform={`scale(${NOTEHEAD_SCALE}, -${NOTEHEAD_SCALE})`}
      />

      {/* SECOND NOTEHEAD */}
      <path
        d={CLOSED_NOTEHEAD_PATH}
        fill={fill}
        transform={`
          translate(${HEAD_SPACING}, 0)
          scale(${NOTEHEAD_SCALE}, -${NOTEHEAD_SCALE})
        `}
      />

      {/* STEMS */}
      <rect
        x={stemX}
        y={stemY}
        width={STEM_WIDTH}
        height={STEM_HEIGHT}
        fill={fill}
      />
      <rect
        x={stemX + HEAD_SPACING}
        y={stemY}
        width={STEM_WIDTH}
        height={STEM_HEIGHT}
        fill={fill}
      />

      {/* BEAM */}
      <rect
        x={stemX - (stemUp ? 0 : BEAM_LENGTH - 2)}
        y={beamY}
        width={BEAM_LENGTH}
        height={BEAM_THICKNESS}
        fill={fill}
      />
    </g>
  );
}

/* ---------------- NOTES ---------------- */

function NoteGlyph({
  type,
  fill,
  stemUp = true,
  accidental = null,
  accidentalOnly = false,
  flagOnly = false,
  suppressStemAndFlag = false,
  suppressFlag = false,
  accidentalOffsetX = -11,
}) {
  const isDotted = type.startsWith("dotted_");
  const baseType = isDotted ? type.replace("dotted_", "") : type;

  let noteheadPath;
  let hasStem = baseType !== "whole_note";
  let hasAccidental = accidental !== null && accidental !== "";
  const isHalf = baseType === "half_note";
  let flagPath = null;
  let flagTransform = null;
  let accidentalPath = null;
  let axeOffset = 0;
  let assOff = accidentalOffsetX ? accidentalOffsetX : 11;
  // console.log(assOff);

  // NOTEHEAD
  if (baseType === "whole_note") {
    noteheadPath = WHOLE_NOTEHEAD_PATH;
  } else if (baseType === "half_note") {
    noteheadPath = OPEN_NOTEHEAD_PATH;
  } else {
    noteheadPath = CLOSED_NOTEHEAD_PATH;
  }

  // FLAGS
  const flagConfig = FLAG_CONFIG[baseType];
  if (flagConfig) {
    flagPath = flagConfig.path;
    if (baseType=="sixteenth_note") {
      //console.log(stemUp);
    }
    flagTransform = stemUp ? flagConfig.up : flagConfig.down;
  }

  // ACCIDENTALS
  let accidentalTransform = `translate(${accidentalOffsetX}, 0) scale(0.03, -0.03)`;

  switch (accidental) {
    case "#":
    case "♯":
      accidentalPath = SHARP_PATH;
      accidentalTransform = `translate(${accidentalOffsetX + 2}, 0) scale(0.03, -0.03)`;
      break;

    case "b":
    case "♭":
      accidentalPath = FLAT_PATH;
      accidentalTransform = `translate(${accidentalOffsetX + 3}, 0) scale(0.03, -0.03)`;
      break;

    case "##":
    case "x":
    case "𝄪":
      accidentalPath = DOUBLE_SHARP_PATH;
      accidentalTransform = `translate(${accidentalOffsetX - 2}, 55) scale(0.18, -0.18)`;
      break;

    case "bb":
    case "ff":
    case "𝄫":
      accidentalPath = DOUBLE_FLAT_PATH;
      accidentalTransform = `translate(${accidentalOffsetX - 13}, -65) scale(0.18, 0.18)`;
      break;

    case "n":
    case "♮":
      accidentalPath = NATURAL_PATH;
      accidentalTransform = `translate(${accidentalOffsetX + 4}, 0) scale(0.03, -0.03)`;
      break;

    default:
      accidentalPath = null;
      break;
  }

  const xOffset = isHalf ? 0 : 0;
  const NSCALE = isHalf ? HALFNOTE_SCALE : NOTEHEAD_SCALE;

  return (
    <g>
      {/* NOTEHEAD */}
      {!accidentalOnly && !flagOnly && (
        <path
          d={noteheadPath}
          fill={fill}
          stroke={isHalf ? fill : "none"}
          strokeWidth={isHalf ? 12 : 0}
          transform={`
            translate(${xOffset}, 0)
            scale(${NSCALE}, -${NSCALE})
          `}
        />
      )}

      {/* AUGMENTATION DOT */}
      {!accidentalOnly && isDotted && (
        <circle
          cx={18}
          cy={-2}
          r={1.8}
          fill={fill}
        />
      )}

      {/* ACCIDENTAL */}
      {hasAccidental && accidentalPath && (
        <g transform={accidentalTransform}>
          <path d={accidentalPath} fill={fill} />
        </g>
      )}

      {/* STEM */}
      {!accidentalOnly && !flagOnly && !suppressStemAndFlag && hasStem && stemUp && (
        <rect x="10.7" y="-35.5" width="1" height="34" fill={fill} />
      )}

      {!accidentalOnly && !flagOnly && !suppressStemAndFlag && hasStem && !stemUp && (
        <rect x="-0.1" y="1.5" width="1" height="34" fill={fill} />
      )}

      {/* FLAG */}
      {!accidentalOnly && !suppressStemAndFlag && !suppressFlag && flagPath && (
        <g transform={flagTransform}>
          <path d={flagPath} fill={fill} />
        </g>
      )}

    </g>
  );
}

/* ---------------- RESTS ---------------- */

function RestGlyph({ type, fill }) {
  const isDotted = type.startsWith("dotted_");
  const baseType = isDotted ? type.replace("dotted_", "") : type;

  const pathMap = {
    quarter_rest: QUARTER_REST_PATH,
    half_rest: HALF_REST_PATH,
    whole_rest: WHOLE_REST_PATH,
    eighth_rest: EIGHTH_REST_PATH,
    sixteenth_rest: SIXTEENTH_REST_PATH,
    thirty_second_rest: THIRTY_SECOND_REST_PATH
  };

  const path = pathMap[baseType];
  if (!path) return null;

  return (
    <g>
      <path
        d={path}
        fill={fill}
        stroke={fill}
        strokeWidth={0}
        transform={`scale(${REST_SCALE},-${REST_SCALE})`}
      />

      {isDotted && (
        <circle
          cx={14}
          cy={-2}
          r={1.8}
          fill={fill}
        />
      )}
    </g>
  );
}
